import { useState, useEffect, useRef } from 'react';
import apiClient from '../services/apiClient';

// Datos mock para fallback en caso de error
const MOCK_PLANES = [
  {
    id: 'revvly_free',
    name: 'Free',
    price: 0,
    priceFormatted: 'Gratis',
    period: '',
    description: 'Plan gratuito para probar Revvly',
    precio_anual: 0,
    descuento_anual: 0,
    limites: {
      sucursales: 1,
      vehiculos: 50,
      mantenimientos: 20,
      almacenamiento: 1,
      usuarios: 10
    },
    beneficios: [],
    popular: false,
    color: 'from-blue-500 to-blue-600',
    icon: '🆓',
    features: ['1 sucursal', '10 usuarios', '50 vehículos', '20 mantenimientos/mes', '1GB almacenamiento', 'Gestión básica de citas', 'Soporte por email']
  },
  {
    id: 'revvly_bronze',
    name: 'Bronze',
    price: 33990,
    priceFormatted: '$33.990',
    period: '/mes',
    description: 'Ideal para talleres pequeños que están comenzando',
    precio_anual: 407880, // 33990 * 12
    descuento_anual: 15,
    limites: {
      sucursales: 3,
      vehiculos: 200,
      mantenimientos: 100,
      almacenamiento: 5,
      usuarios: 50
    },
    beneficios: [],
    popular: false,
    color: 'from-amber-600 to-yellow-700',
    icon: '🔧',
    features: ['3 sucursales', '10 usuarios', '200 vehículos', '100 mantenimientos/mes', '5GB almacenamiento', 'Gestión completa de talleres', 'Reportes avanzados', 'Soporte prioritario']
  },
  {
    id: 'revvly_silver',
    name: 'Silver',
    price: 65990,
    priceFormatted: '$65.990',
    period: '/mes',
    description: 'Perfecto para talleres en crecimiento',
    precio_anual: 791880, // 65990 * 12
    descuento_anual: 20,
    limites: {
      sucursales: 10,
      vehiculos: 1000,
      mantenimientos: 500,
      almacenamiento: 25,
      usuarios: 50
    },
    beneficios: [],
    popular: true,
    color: 'from-gray-400 to-gray-500',
    icon: '🥉',
    features: ['10 sucursales', '50 usuarios', '1000 vehículos', '500 mantenimientos/mes', '25GB almacenamiento', 'IA avanzada + personalización', 'Multi-sucursal', 'Analytics avanzado', 'API completa', 'Soporte 24/7']
  },
  {
    id: 'revvly_gold',
    name: 'Gold',
    price: 129990,
    priceFormatted: '$129.990',
    period: '/mes',
    description: 'Para redes de talleres y empresas grandes',
    precio_anual: 1559880, // 129990 * 12
    descuento_anual: 25,
    limites: {
      sucursales: -1,
      vehiculos: -1,
      mantenimientos: -1,
      almacenamiento: 100,
      usuarios: -1
    },
    beneficios: [],
    popular: false,
    color: 'from-yellow-400 to-amber-500',
    icon: '🥇',
    features: ['Sucursales ilimitadas', 'Usuarios ilimitados', 'Vehículos ilimitados', 'Mantenimientos ilimitados', '100GB almacenamiento', 'Todas las funcionalidades', 'API ilimitada', 'Integraciones premium', 'Soporte dedicado', 'Consultoría']
  }
];

// SINGLETON GLOBAL para evitar múltiples inicializaciones
class PlanesStore {
  constructor() {
    this.planes = [];
    this.loading = false;
    this.error = null;
    this.initialized = false;
    this.initPromise = null;
    this.subscribers = new Set();
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  notify() {
    this.subscribers.forEach(callback => callback({
      planes: this.planes,
      loading: this.loading,
      error: this.error
    }));
  }

  async initialize() {
    // Si ya está inicializado, no hacer nada
    if (this.initialized) {
      return;
    }

    // Si ya hay una inicialización en progreso, esperarla
    if (this.initPromise) {
      return this.initPromise;
    }

    // Crear nueva promesa de inicialización
    this.initPromise = this._fetchPlanes();
    await this.initPromise;
    this.initPromise = null;
  }

  async _fetchPlanes() {
    try {
      this.loading = true;
      this.error = null;
      this.notify();

      //console.log('🔍 Inicializando planes desde API...');

      // Usar el endpoint API en lugar de Supabase directamente
      const response = await apiClient.get('/api/planes');

      if (!response.data?.success) {
        throw new Error('Error obteniendo planes desde API');
      }

      const planesData = response.data.data;

      if (!planesData || planesData.length === 0) {
        console.warn('⚠️ No se encontraron planes en la API');
        this.planes = [];
        this.initialized = true;
        this.loading = false;
        this.notify();
        return;
      }

      // Los datos ya vienen formateados desde la API
      //console.log('✅ Planes cargados exitosamente desde API:', planesData.length);
      this.planes = planesData;
      this.initialized = true;
    } catch (error) {
      console.error('❌ Error obteniendo planes desde API:', error);
      console.warn('🔄 Usando datos mock como fallback...');
      
      // Usar datos mock como fallback
      this.planes = MOCK_PLANES;
      this.error = null; // No mostrar error si tenemos fallback
      this.initialized = true;
    } finally {
      this.loading = false;
      this.notify();
    }
  }

  refetch() {
    this.initialized = false;
    return this.initialize();
  }
}

// Instancia singleton
const planesStore = new PlanesStore();

/**
 * Hook para obtener los planes disponibles desde la base de datos
 * Utiliza un singleton para evitar múltiples llamadas y re-renders
 */
export const usePlanes = () => {
  const [state, setState] = useState({
    planes: planesStore.planes,
    loading: planesStore.loading,
    error: planesStore.error
  });
  
  //console.log('✈️ usePlanes called - timestamp:', new Date().toLocaleTimeString(), 'planes.length:', state.planes.length, 'loading:', state.loading);

  const mountedRef = useRef(true);

  useEffect(() => {
    // Suscribirse a cambios en el store
    const unsubscribe = planesStore.subscribe((newState) => {
      if (mountedRef.current) {
        setState(newState);
      }
    });

    // Inicializar si es necesario
    planesStore.initialize();

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, []); // Solo ejecutar una vez

  return { 
    planes: state.planes, 
    loading: state.loading, 
    error: state.error, 
    refetch: () => planesStore.refetch() 
  };
};

// Helper functions
const getPlanColor = (planId) => {
  const colors = {
    revvly_free: 'from-blue-500 to-blue-600',
    revvly_bronze: 'from-amber-600 to-yellow-700',
    revvly_silver: 'from-gray-400 to-gray-500',
    revvly_gold: 'from-yellow-400 to-amber-500'
  };
  return colors[planId] || 'from-blue-500 to-blue-600';
};

const getPlanIcon = (planId) => {
  const icons = {
    revvly_free: '🆓',
    revvly_bronze: '🔧',
    revvly_silver: '🥉',
    revvly_gold: '🥇'
  };
  return icons[planId] || '🔧';
};

const getFeaturesFromBeneficios = (beneficios, plan) => {
  const features = [];
  
  // Primero agregar los límites básicos del plan
  if (plan.max_sucursales === -1) {
    features.push('Sucursales ilimitadas');
  } else {
    features.push(`${plan.max_sucursales} sucursal${plan.max_sucursales > 1 ? 'es' : ''}`);
  }

  if (plan.max_vehiculos === -1) {
    features.push('Vehículos ilimitados');
  } else {
    features.push(`${plan.max_vehiculos} vehículos`);
  }

  if (plan.max_mantenimientos_mes === -1) {
    features.push('Mantenimientos ilimitados');
  } else {
    features.push(`${plan.max_mantenimientos_mes} mantenimientos/mes`);
  }

  if (plan.max_almacenamiento_gb) {
    features.push(`${plan.max_almacenamiento_gb}GB almacenamiento`);
  }

  // Luego agregar los beneficios de la base de datos
  if (beneficios && beneficios.length > 0) {
    beneficios.forEach(beneficio => {
      if (beneficio.incluido) {
        // Agregar solo el texto del título, no el objeto completo
        features.push(beneficio.titulo);
      }
    });
  }
  
  return features;
};

export default usePlanes;